import { EM_DASH } from '../../lib/typography';
import { Component } from 'preact';
import { api } from '../../utils/serverClient';
import { searchPlayers, sendFriendRequest } from '../../utils/friendsApi';
import {
  GOLD, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  INPUT_STYLE, DANGER_BTN,
} from '../../lib/medievalTheme';
import { Section, AdminButton } from '../AdminPanel';
import { hasCap } from '../../utils/staffAccess';

// Player management is one search and one selection. Every action a
// moderator holds acts on the player currently selected. There is no
// second name field anywhere, because a moderator typing the same name
// into three boxes is three chances to action the wrong account.
//
// Selecting a player loads their full record (view-as) and, when the
// account holds player.audit, their economy timeline in the same pass.

const SEARCH_DEBOUNCE_MS = 220;
const MIN_QUERY = 2;

function levelFromXp(xp) {
  return Math.max(1, Math.floor((xp || 0) / 1000) + 1);
}

export default class AdminPlayersTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Search + selection
      query: '',
      suggestions: [],
      searching: false,
      showSuggestions: false,
      selected: null,        // full view-as record
      selectedLoading: false,
      selectError: null,
      // Actions on the selected player
      actionBusy: null,      // 'ban' | 'rename' | 'friend' | null
      actionError: null,
      banAction: 'ban',
      muteAction: 'mute',
      muteReason: '',
      muteDuration: 1,
      banReason: '',
      banDuration: 24,
      renameNew: '',
      // Audit + logs for the selected player
      audit: null,
      auditLoading: false,
      logRoom: null,
      logLoading: false,
      logResult: null,
      logError: null,
      logFilter: '',
    };
    this._searchTimer = null;
  }

  componentWillUnmount() {
    clearTimeout(this._searchTimer);
  }

  // ─── Search + select ─────────────────────────────────
  handleQueryInput = (value) => {
    this.setState({ query: value, showSuggestions: true });
    clearTimeout(this._searchTimer);
    if (value.trim().length < MIN_QUERY) {
      this.setState({ suggestions: [], searching: false });
      return;
    }
    this.setState({ searching: true });
    this._searchTimer = setTimeout(() => this.runSearch(value.trim()), SEARCH_DEBOUNCE_MS);
  };

  runSearch = async (q) => {
    try {
      const results = await searchPlayers(q);
      // Ignore a response that lost the race with newer typing.
      if (this.state.query.trim() !== q) return;
      this.setState({ suggestions: results || [], searching: false });
    } catch {
      this.setState({ suggestions: [], searching: false });
    }
  };

  handleSelect = async (name) => {
    const target = (name || '').trim();
    if (!target) return;
    this.setState({
      query: target,
      showSuggestions: false,
      suggestions: [],
      selectedLoading: true,
      selected: null,
      selectError: null,
      actionError: null,
      audit: null,
      logRoom: null,
      logResult: null,
      renameNew: '',
      banReason: '',
    });
    try {
      const record = await api.post('/admin/view-as', { name: target });
      this.setState({ selected: record, selectedLoading: false });
      this.props.addLog(`Selected ${record.name || target}`);
      if (hasCap(this.props.profile, 'player.audit')) this.loadAudit(record.name || target);
    } catch (e) {
      this.setState({ selectError: e.message, selectedLoading: false });
    }
  };

  // Re-fetch the selected record after an action so ban status and
  // name on screen are never stale.
  refreshSelected = async (name) => {
    try {
      const record = await api.post('/admin/view-as', { name });
      this.setState({ selected: record, query: record.name || name });
    } catch { /* the action already reported its own outcome */ }
  };

  loadAudit = async (name) => {
    this.setState({ auditLoading: true });
    try {
      const audit = await api.get(`/admin/player-audit?name=${encodeURIComponent(name)}`);
      this.setState({ audit, auditLoading: false });
    } catch {
      this.setState({ audit: null, auditLoading: false });
    }
  };

  clearSelection = () => {
    this.setState({
      query: '', suggestions: [], selected: null, audit: null,
      logRoom: null, logResult: null, selectError: null, actionError: null,
    });
  };

  // ─── Actions on the selected player ──────────────────
  handleBanAction = async () => {
    const { selected, banAction, banReason, banDuration } = this.state;
    if (!selected?.name) return;
    this.setState({ actionBusy: 'ban', actionError: null });
    try {
      const body = { name: selected.name, action: banAction, reason: banReason || undefined };
      if (banAction === 'suspend') body.duration = parseInt(banDuration, 10) || 24;
      await api.post('/admin/ban-player', body);
      this.props.addLog(`${banAction} executed on ${selected.name}`);
      this.setState({ actionBusy: null, banReason: '' });
      await this.refreshSelected(selected.name);
    } catch (e) {
      this.setState({ actionBusy: null, actionError: e.message });
      this.props.addLog(`Ban action failed: ${e.message}`);
    }
  };

  handleMuteAction = async () => {
    const { selected, muteAction, muteReason, muteDuration } = this.state;
    if (!selected?.name) return;
    this.setState({ actionBusy: 'mute', actionError: null });
    try {
      const body = { name: selected.name, action: muteAction, reason: muteReason || undefined };
      if (muteAction === 'mute') body.duration = parseInt(muteDuration, 10) || 1;
      await api.post('/admin/mute-player', body);
      this.props.addLog(`${muteAction} executed on ${selected.name}`);
      this.setState({ actionBusy: null, muteReason: '' });
      await this.refreshSelected(selected.name);
    } catch (e) {
      this.setState({ actionBusy: null, actionError: e.message });
      this.props.addLog(`Mute action failed: ${e.message}`);
    }
  };

  handleRename = async () => {
    const { selected, renameNew } = this.state;
    if (!selected?.name || !renameNew.trim()) return;
    this.setState({ actionBusy: 'rename', actionError: null });
    try {
      const data = await api.post('/admin/rename-player', {
        currentName: selected.name,
        newName: renameNew.trim(),
      });
      this.props.addLog(`Renamed ${data.oldName} → ${data.newName}`);
      this.setState({ actionBusy: null, renameNew: '' });
      await this.refreshSelected(data.newName);
    } catch (e) {
      this.setState({ actionBusy: null, actionError: e.message });
      this.props.addLog(`Rename failed: ${e.message}`);
    }
  };

  handleSendFriendRequest = async () => {
    const { selected } = this.state;
    if (!selected?.id) return;
    this.setState({ actionBusy: 'friend', actionError: null });
    try {
      await sendFriendRequest(selected.id);
      this.props.addLog(`Friend request sent to ${selected.name}`);
    } catch (e) {
      this.setState({ actionError: e.message });
    }
    this.setState({ actionBusy: null });
  };

  handleOpenLog = async (roomCode) => {
    this.setState({ logRoom: roomCode, logLoading: true, logResult: null, logError: null });
    try {
      const result = await api.get(`/admin/match-log/${encodeURIComponent(roomCode)}`);
      this.setState({ logResult: result, logLoading: false });
      this.props.addLog(`Opened match log ${roomCode} (${result.source}, ${result.events.length} events)`);
    } catch (e) {
      this.setState({ logError: e.message, logLoading: false });
    }
  };

  // A match_history row above the formula's ceiling. XP is exact.
  // nothing but the reward formula writes xp_earned. Coins are a soft
  // signal because the level-up bonus lands in the same column, so a
  // coin flag without an XP flag is worth a look, not a ban.
  matchFlags(row, ceilings) {
    return {
      xp: !!ceilings && row.xp_earned > ceilings.xp,
      coins: !!ceilings && row.coins_earned > ceilings.coins,
    };
  }

  render() {
    const { profile } = this.props;
    const { selected } = this.state;

    return (
      <>
        <Section title="Player">
          {this.renderSearch()}
          {this.state.selectError && (
            <div className="mt-2 text-xs" style={{ color: '#c45050' }}>{this.state.selectError}</div>
          )}
          {this.state.selectedLoading && (
            <div className="mt-2 text-xs" style={{ color: TEXT_MUTED }}>Loading…</div>
          )}
          {selected && this.renderSelected()}
        </Section>

        {selected && this.state.logRoom && hasCap(profile, 'match.logs') && (
          <Section title={`Match log. ${this.state.logRoom}`}>
            {this.renderMatchLog()}
          </Section>
        )}
      </>
    );
  }

  renderSearch() {
    const { query, suggestions, searching, showSuggestions, selected } = this.state;
    const open = showSuggestions && query.trim().length >= MIN_QUERY;

    return (
      <div className="relative">
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search a player by name…"
            value={query}
            onInput={(e) => this.handleQueryInput(e.target.value)}
            onFocus={() => this.setState({ showSuggestions: true })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') this.handleSelect(suggestions[0]?.name || query);
              if (e.key === 'Escape') this.setState({ showSuggestions: false });
            }}
            className="flex-1 px-3 py-1.5 text-xs"
            style={INPUT_STYLE}
          />
          {selected && <AdminButton onClick={this.clearSelection}>Clear</AdminButton>}
        </div>

        {open && (
          <div
            className="absolute left-0 right-0 mt-1 z-10 flex flex-col rounded overflow-hidden"
            style={{ background: '#171310', border: `1px solid ${GOLD} 0.25)` }}
          >
            {searching && suggestions.length === 0 ? (
              <span className="px-3 py-2 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>Searching…</span>
            ) : suggestions.length === 0 ? (
              <span className="px-3 py-2 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>No players found</span>
            ) : suggestions.slice(0, 8).map((p) => (
              <button
                key={p.id}
                type="button"
                className="px-3 py-1.5 text-left text-[0.6875cqw] transition-colors hover:brightness-125"
                style={{ background: 'transparent', color: TEXT_BODY }}
                onClick={() => this.handleSelect(p.name)}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderSelected() {
    const { profile } = this.props;
    const { selected, actionError } = this.state;
    const banned = selected.isBanned || selected.banned;

    return (
      <div className="mt-3 flex flex-col gap-3">
        {/* Identity + state */}
        <div className="grid grid-cols-4 gap-x-4 gap-y-1 text-[0.6875cqw]" style={{ color: TEXT_BODY }}>
          <div><span style={{ color: TEXT_MUTED }}>Name:</span> {selected.name}</div>
          <div><span style={{ color: TEXT_MUTED }}>Level:</span> {levelFromXp(selected.xp)}</div>
          <div><span style={{ color: TEXT_MUTED }}>Gold:</span> {selected.coins ?? 0}</div>
          <div><span style={{ color: TEXT_MUTED }}>Record:</span> {selected.wins ?? 0}W / {selected.losses ?? 0}L</div>
          <div>
            <span style={{ color: TEXT_MUTED }}>Rank:</span>{' '}
            {selected.tier ? `${selected.tier} ${selected.division ?? ''} (${selected.lp ?? 0} LP)` : 'Unranked'}
          </div>
          <div>
            <span style={{ color: TEXT_MUTED }}>Collection:</span>{' '}
            {(selected.collection || []).reduce((s, c) => s + (c.quantity || 0), 0)}
          </div>
          <div><span style={{ color: TEXT_MUTED }}>Decks:</span> {(selected.decks || []).length}</div>
          <div>
            <span style={{ color: TEXT_MUTED }}>Plays via:</span>{' '}
            {selected.last_platform === 'web' ? 'browser' : selected.last_platform === 'desktop' ? 'desktop app' : 'unknown'}
          </div>
          <div>
            <span style={{ color: TEXT_MUTED }}>Status:</span>{' '}
            {banned
              ? <span style={{ color: '#c45050' }}>
                  Banned{selected.banReason ? ` (${selected.banReason})` : ''}
                  {selected.bannedUntil ? ` until ${new Date(selected.bannedUntil).toLocaleDateString()}` : ''}
                </span>
              : selected.isMuted
                ? <span style={{ color: ACCENT_GOLD }}>
                    Muted{selected.muteReason ? ` (${selected.muteReason})` : ''}
                    {selected.mutedUntil ? ` until ${new Date(selected.mutedUntil).toLocaleString()}` : ''}
                  </span>
                : <span style={{ color: '#22c55e' }}>Clean</span>}
          </div>
        </div>

        {selected.isStaff && (
          <div className="text-[0.6875cqw]" style={{ color: ACCENT_GOLD }}>
            Staff account. Protected from ban and rename. Revoke the role in Settings first.
          </div>
        )}

        {actionError && (
          <div className="text-[0.6875cqw]" style={{ color: '#c45050' }}>{actionError}</div>
        )}

        {this.renderActions()}
        {hasCap(profile, 'player.audit') && this.renderAudit()}
      </div>
    );
  }

  renderActions() {
    const { profile } = this.props;
    const {
      selected, actionBusy, banAction, banReason, banDuration,
      muteAction, muteReason, muteDuration, renameNew,
    } = this.state;
    const protectedTarget = !!selected.isStaff;

    return (
      <div className="flex flex-col gap-2 pt-2" style={{ borderTop: `1px solid ${GOLD} 0.1)` }}>
        {hasCap(profile, 'player.ban') && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[0.625cqw] w-20 shrink-0" style={{ color: TEXT_MUTED }}>Enforce</span>
            <select
              value={banAction}
              onChange={(e) => this.setState({ banAction: e.target.value })}
              className="px-2 py-1 text-xs"
              style={{ ...INPUT_STYLE, color: TEXT_BODY }}
            >
              <option value="ban">Ban (permanent)</option>
              <option value="suspend">Suspend (temporary)</option>
              <option value="unban">Unban</option>
            </select>
            <input
              type="text"
              placeholder="Reason"
              value={banReason}
              onInput={(e) => this.setState({ banReason: e.target.value })}
              className="flex-1 px-3 py-1 text-xs"
              style={INPUT_STYLE}
            />
            {banAction === 'suspend' && (
              <input
                type="number"
                min="1"
                placeholder="Hours"
                value={banDuration}
                onInput={(e) => this.setState({ banDuration: e.target.value })}
                className="w-20 px-2 py-1 text-xs text-center"
                style={INPUT_STYLE}
              />
            )}
            <button
              type="button"
              className="px-3 py-1.5 text-[0.6875cqw] font-semibold transition-all hover:scale-[1.03] active:scale-[0.97] disabled:opacity-40"
              style={banAction === 'unban' ? undefined : DANGER_BTN}
              onClick={this.handleBanAction}
              disabled={actionBusy === 'ban' || (protectedTarget && banAction !== 'unban')}
            >
              <span style={{ color: banAction === 'unban' ? '#22c55e' : undefined }}>
                {actionBusy === 'ban' ? 'Executing…' : banAction === 'unban' ? 'Unban' : banAction === 'suspend' ? 'Suspend' : 'Ban'}
              </span>
            </button>
          </div>
        )}

        {hasCap(profile, 'chat.moderate') && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[0.625cqw] w-20 shrink-0" style={{ color: TEXT_MUTED }}>Tavern</span>
            <select
              value={muteAction}
              onChange={(e) => this.setState({ muteAction: e.target.value })}
              className="px-2 py-1 text-xs"
              style={{ ...INPUT_STYLE, color: TEXT_BODY }}
            >
              <option value="mute">Mute (temporary)</option>
              <option value="unmute">Unmute</option>
            </select>
            <input
              type="text"
              placeholder="Reason"
              value={muteReason}
              onInput={(e) => this.setState({ muteReason: e.target.value })}
              className="flex-1 px-3 py-1 text-xs"
              style={INPUT_STYLE}
            />
            {muteAction === 'mute' && (
              <input
                type="number"
                min="1"
                placeholder="Hours"
                value={muteDuration}
                onInput={(e) => this.setState({ muteDuration: e.target.value })}
                className="w-20 px-2 py-1 text-xs text-center"
                style={INPUT_STYLE}
              />
            )}
            <button
              type="button"
              className="px-3 py-1.5 text-[0.6875cqw] font-semibold transition-all hover:scale-[1.03] active:scale-[0.97] disabled:opacity-40"
              style={muteAction === 'unmute' ? undefined : DANGER_BTN}
              onClick={this.handleMuteAction}
              disabled={actionBusy === 'mute' || (protectedTarget && muteAction !== 'unmute')}
            >
              <span style={{ color: muteAction === 'unmute' ? '#22c55e' : undefined }}>
                {actionBusy === 'mute' ? 'Executing…' : muteAction === 'unmute' ? 'Unmute' : 'Mute'}
              </span>
            </button>
          </div>
        )}

        {hasCap(profile, 'player.rename') && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[0.625cqw] w-20 shrink-0" style={{ color: TEXT_MUTED }}>Rename</span>
            <input
              type="text"
              placeholder="New name"
              value={renameNew}
              onInput={(e) => this.setState({ renameNew: e.target.value })}
              className="flex-1 px-3 py-1 text-xs"
              style={INPUT_STYLE}
            />
            <AdminButton
              onClick={this.handleRename}
              disabled={actionBusy === 'rename' || !renameNew.trim() || protectedTarget}
            >
              {actionBusy === 'rename' ? 'Renaming…' : 'Rename'}
            </AdminButton>
          </div>
        )}

        <div className="flex items-center gap-2">
          <span className="text-[0.625cqw] w-20 shrink-0" style={{ color: TEXT_MUTED }}>Social</span>
          <AdminButton onClick={this.handleSendFriendRequest} disabled={actionBusy === 'friend'}>
            {actionBusy === 'friend' ? 'Sending…' : 'Send friend request'}
          </AdminButton>
        </div>
      </div>
    );
  }

  renderAudit() {
    const { audit, auditLoading } = this.state;
    if (auditLoading) return <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>Loading audit…</span>;
    if (!audit) return null;
    const { ceilings, totals, matches, daily, auctions, loggedRooms } = audit;
    const flagged = matches.filter((m) => {
      const f = this.matchFlags(m, ceilings);
      return f.xp || f.coins;
    }).length;

    return (
      <div className="flex flex-col gap-3 pt-2" style={{ borderTop: `1px solid ${GOLD} 0.1)` }}>
        <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-[0.6875cqw]" style={{ color: TEXT_BODY }}>
          <div><span style={{ color: TEXT_MUTED }}>From matches:</span> {totals.coinsFromMatches}g · {totals.xpFromMatches}xp</div>
          <div><span style={{ color: TEXT_MUTED }}>Auction:</span> +{totals.auctionCoinsIn} / −{totals.auctionCoinsOut}</div>
          <div><span style={{ color: TEXT_MUTED }}>Matches:</span> {totals.matches}</div>
          <div className="col-span-2">
            <span style={{ color: TEXT_MUTED }}>Per-match ceiling:</span> {ceilings.coins}g / {ceilings.xp}xp
            {flagged > 0
              ? <span style={{ color: '#c45050' }}> · {flagged} over</span>
              : <span style={{ color: '#22c55e' }}> · none over</span>}
          </div>
          <div>
            <span style={{ color: TEXT_MUTED }}>First match:</span>{' '}
            {totals.firstMatch ? new Date(totals.firstMatch).toLocaleDateString() : EM_DASH}
          </div>
        </div>

        <div>
          <div className="text-[0.625cqw] mb-1" style={{ color: TEXT_MUTED }}>
            Matches per day (14d). A flat wall of days well above this player's own baseline is the farming signal.
          </div>
          <div className="flex flex-wrap gap-1">
            {daily.length === 0 ? (
              <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>No matches in the last 14 days</span>
            ) : daily.map((d) => (
              <span key={d.day} className="text-[0.625cqw] px-2 py-0.5 rounded" style={{ background: `${GOLD} 0.08)`, color: TEXT_BODY }}>
                {new Date(d.day).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}: {d.matches} · {d.coins}g
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[0.625cqw] mb-1" style={{ color: TEXT_MUTED }}>
            Recent matches. XP above ceiling is impossible; gold above ceiling can be a legitimate level-up bonus, so check the XP on the same row.
          </div>
          <div className="flex flex-col gap-0.5 max-h-40 overflow-y-auto">
            {matches.map((m, i) => {
              const f = this.matchFlags(m, ceilings);
              return (
                <div key={i} className="text-[0.625cqw] flex gap-3" style={{ color: f.xp ? '#c45050' : TEXT_BODY }}>
                  <span style={{ color: TEXT_MUTED }} className="w-24 shrink-0">{new Date(m.played_at).toLocaleDateString()}</span>
                  <span className="w-8 shrink-0">{m.won ? 'W' : 'L'}</span>
                  <span className="flex-1 truncate">vs {m.opponent_name || EM_DASH}</span>
                  <span className="w-20 shrink-0" style={{ color: f.coins ? '#c45050' : undefined }}>{m.coins_earned}g</span>
                  <span className="w-20 shrink-0" style={{ color: f.xp ? '#c45050' : undefined }}>{m.xp_earned}xp</span>
                </div>
              );
            })}
          </div>
        </div>

        {auctions.sales.length > 0 || auctions.purchases.length > 0 ? (
          <div>
            <div className="text-[0.625cqw] mb-1" style={{ color: TEXT_MUTED }}>
              Auction flow. A common card changing hands for a large sum between two accounts is a coin transfer, not a trade.
            </div>
            <div className="flex flex-col gap-0.5 max-h-32 overflow-y-auto">
              {auctions.sales.map((s, i) => (
                <div key={`s${i}`} className="text-[0.625cqw] flex gap-3" style={{ color: TEXT_BODY }}>
                  <span className="w-14 shrink-0" style={{ color: '#22c55e' }}>Sold</span>
                  <span className="flex-1 truncate">{s.card_name || s.card_id}{s.foiling === 'F' ? ' (foil)' : ''}</span>
                  <span className="w-20 shrink-0">{s.price}g</span>
                  <span className="w-20 shrink-0" style={{ color: TEXT_MUTED }}>{s.status}</span>
                </div>
              ))}
              {auctions.purchases.map((p, i) => (
                <div key={`p${i}`} className="text-[0.625cqw] flex gap-3" style={{ color: TEXT_BODY }}>
                  <span className="w-14 shrink-0" style={{ color: '#c45050' }}>Bought</span>
                  <span className="flex-1 truncate">{p.card_name || p.card_id} from {p.seller_name || EM_DASH}</span>
                  <span className="w-20 shrink-0">{p.price}g</span>
                  <span className="w-20 shrink-0" />
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {hasCap(this.props.profile, 'match.logs') && loggedRooms.length > 0 ? (
          <div>
            <div className="text-[0.625cqw] mb-1" style={{ color: TEXT_MUTED }}>
              Matches with a stored log. Opening one shows both seats. Full information, and the read is recorded.
            </div>
            <div className="flex flex-wrap gap-1">
              {loggedRooms.map((r) => (
                <AdminButton key={r.room_code} onClick={() => this.handleOpenLog(r.room_code)}>
                  {r.room_code} · {new Date(r.recorded_at).toLocaleDateString()}
                </AdminButton>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  renderMatchLog() {
    const { logLoading, logResult, logError, logFilter } = this.state;
    if (logLoading) return <span className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>Loading…</span>;
    if (logError) return <span className="text-[0.6875cqw]" style={{ color: '#c45050' }}>{logError}</span>;
    if (!logResult) return null;

    const filter = logFilter.trim().toLowerCase();
    const events = filter
      ? logResult.events.filter((e) => JSON.stringify(e).toLowerCase().includes(filter))
      : logResult.events;

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
            {logResult.source === 'merged'
              ? 'Merged, both seats.'
              : `Staged, seat${logResult.seats?.length === 1 ? '' : 's'} ${logResult.seats?.join(' + ')}. The match never reconciled.`}
            {' '}{events.length} of {logResult.events.length} events.
          </span>
          {logResult.divergence ? (
            <span className="text-[0.625cqw] px-2 py-0.5 rounded" style={{ background: 'rgba(196,80,80,0.15)', color: '#c45050' }}>
              seats disagreed
            </span>
          ) : null}
          <input
            type="text"
            placeholder="Filter events…"
            value={logFilter}
            onInput={(e) => this.setState({ logFilter: e.target.value })}
            className="px-2 py-1 text-xs flex-1"
            style={INPUT_STYLE}
          />
          <AdminButton onClick={() => this.setState({ logRoom: null, logResult: null, logFilter: '' })}>Close</AdminButton>
        </div>
        <div className="flex flex-col gap-0.5 max-h-52 overflow-y-auto font-mono">
          {events.map((e, i) => (
            <div key={i} className="text-[0.5625cqw] flex gap-2" style={{ color: TEXT_BODY }}>
              <span className="w-10 shrink-0" style={{ color: TEXT_MUTED }}>{i}</span>
              <span className="w-8 shrink-0" style={{ color: TEXT_MUTED }}>{e.dir || ''}</span>
              <span className="w-36 shrink-0">{e.type}</span>
              <span className="flex-1 truncate" style={{ color: TEXT_MUTED }}>
                {e.data ? JSON.stringify(e.data) : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
}
